import React, { useState } from 'react'; 
import axios from 'axios';
import { biosdkOperationUrl, CONVERTER_SINGLE_API_BASE, logBiosdkUiRoute } from './config';
import { Fingerprint, Upload, Loader2, AlertCircle, Clock, Gauge } from 'lucide-react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Label } from './components/ui/label';
import { Select } from './components/ui/select';
import { Badge } from './components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from './components/ui/alert';

function FingerQualityChecker() {
  const [file, setFile] = useState(null);
  const [injpegImageUrl, setInJpegImageUrl] = useState(null);
  const [timing, setTiming] = useState({ convertInput: 0, convertIso: 0, Quality: 0});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [score, setScore] = useState(null); 
  const [selectedOption, setSelectedOption] = useState('');
  const [jp2isoImageUrl, setjp2IsoImageUrl] = useState(null);
  const [analyticsInfo, setAnalyticsInfo] = useState(null); 

  const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  const normalizeAnalyticsInfo = (raw) => {
    if (raw == null) return {};
    if (Array.isArray(raw)) {
      const out = {};
      for (const item of raw) {
        if (item && typeof item === 'object') {
          const key = item.key ?? item.name ?? item.k;
          const val = item.value ?? item.val ?? item.v;
          if (key != null) out[String(key)] = val;
        }
      }
      return out;
    }
    if (typeof raw === 'object') return raw;
    return {};
  };

  const getAnalyticsValue = (obj, ...names) => {
    const lower = names.map((n) => n.toLowerCase());
    for (const key of Object.keys(obj || {})) {
      if (lower.includes(String(key).toLowerCase())) return obj[key];
    }
    return undefined;
  };

  const convertFingerImageToIso = async (inputFile, subType) => {
    const formData = new FormData();
    formData.append('file', inputFile);
    const candidateUrls = [
      `${CONVERTER_SINGLE_API_BASE}/convertImageFileToIso?modality=FINGER&subType=${subType}`,
      `${CONVERTER_SINGLE_API_BASE}/convertImageFileToIso?modality=FINGER&subType=UNKNOWN&purpose=AUTH`,
      `${CONVERTER_SINGLE_API_BASE}/convertImageFileToIso?modality=FINGER&subType=UNKNOWN`,
    ];
    let lastError = null;
    for (const url of candidateUrls) {
      try {
        return await axios.post(url, formData, { responseType: 'arraybuffer' });
      } catch (err) {
        lastError = err;
      }
    }
    throw lastError;
  };

  const handleFileChange = async (e) => {
    const subType = selectedOption.split('|')[0]; 
    const selectedFile = e.target.files[0];
    setFile(selectedFile);

    if (!selectedFile) {
      setError('No file selected.');
      return;
    }

    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    setInJpegImageUrl(null); 

    try {
      if (['jpeg', 'jpg', 'bmp', 'png', 'wsq'].includes(fileExtension)) {
        setInJpegImageUrl(URL.createObjectURL(selectedFile));
      } else if (fileExtension === 'iso') {
        const isoFormData = new FormData();
        isoFormData.append('file', selectedFile);
        const response = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertFromIsoFile?modality=FINGER`, isoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'arraybuffer',
        });
        const jpegBlob = new Blob([response.data], { type: 'image/jpeg' });
        setInJpegImageUrl(URL.createObjectURL(jpegBlob));
        setjp2IsoImageUrl(null);
      } else if (fileExtension === 'jp2') {
        setError('JP2 is not supported in direct mode. Use jpg/jpeg/png/wsq/iso.');
        setFile(null);
        return;
      } else {
        setError('Unsupported file type.');
        setFile(null);
        return;
      }
   
      setScore(null);
      setShowAdvanced(false);
      setError(null);
      setSubmitted(false);
    } catch (err) {
      setError('An error occurred while processing the file.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setIsProcessing(true);

    if (!file || !selectedOption) {
      setError('Please upload a file and select a finger type.');
      setIsProcessing(false);
      return;
    }

    const fileExtension = file.name.split('.').pop().toLowerCase();
    let facebdbPlaceholder = [];
    let startConvertInput = 0;
    let convertInputDuration = 0;
    const subType = selectedOption.split('|')[0]; 

    try {
      if (fileExtension === 'iso') {
        const isoArrayBuffer = await file.arrayBuffer();
        const endConvertInput = performance.now();
        convertInputDuration = endConvertInput - startConvertInput;
        facebdbPlaceholder = JSON.stringify(Array.from(new Uint8Array(isoArrayBuffer)));
      } else if (fileExtension === 'jpeg' || fileExtension === 'jpg'|| fileExtension === 'bmp'|| fileExtension === 'png' || fileExtension === 'wsq') {
        const isoResp = await convertFingerImageToIso(file, subType);
        const isoArrayBuffer = isoResp.data;
        const endConvertInput = performance.now();
        convertInputDuration = endConvertInput - startConvertInput;
        facebdbPlaceholder = JSON.stringify(Array.from(new Uint8Array(isoArrayBuffer)));
      } else {
        throw new Error('Unsupported file type. Use jpg/jpeg/png/bmp/wsq/iso.');
      }

      const templateResponse = await fetch('/finger-quality-request.json');
      if (!templateResponse.ok) throw new Error(`Template fetch failed with status: ${templateResponse.status}`);

      const templateText = await templateResponse.text();
      const [position, finger] = selectedOption.split('|')[1].split(' '); 

      const updatedTemplate = templateText
        .replace('<Left_IndexFinger_BDB>', facebdbPlaceholder) 
        .replace('<Left>', position)
        .replace('<IndexFinger>', finger);

      const versionRequestTemplateResponse = await fetch('/extract_request-template.json');
      if (!versionRequestTemplateResponse.ok) throw new Error(`Version request template fetch failed with status: ${versionRequestTemplateResponse.status}`);

      const versionRequestTemplateText = await versionRequestTemplateResponse.text();
      const base64EncodedExtract = arrayBufferToBase64(new TextEncoder().encode(updatedTemplate));
      const finalRequestBodyExtract = JSON.parse(versionRequestTemplateText.replace('encoded_request', base64EncodedExtract));
      finalRequestBodyExtract.operation = 'check-quality';
      
      const isRawInput = fileExtension !== 'iso';
      const qualityUrl = biosdkOperationUrl('process', isRawInput);
      logBiosdkUiRoute('FingerQuality', isRawInput ? 'rawRoute' : 'isoRoute', qualityUrl, {
        operation: 'check-quality',
      });
      const extractResponse = await axios.post(qualityUrl, finalRequestBodyExtract, {
        headers: { 'Content-Type': 'application/json' },
      });

      const extractResult = extractResponse.data?.response?.response?.scores?.FINGER; 
      if (!extractResult) throw new Error('Failed to extract the score from the response');

      const { score, analyticsInfo } = extractResult;

      setScore(score);
      setAnalyticsInfo(normalizeAnalyticsInfo(analyticsInfo));

      setTiming({
        convertInput: timing.convertInput,
        convertIso: convertInputDuration,
        Quality: 0,
      });
      setShowAdvanced(true);
      setError(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred during the extraction process';
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setSelectedOption('');
    setInJpegImageUrl(null);
    setScore(null);
    setAnalyticsInfo(null);
    setError(null);
    setSubmitted(false);
    setShowAdvanced(false);
  };

  const getQualityLevel = (score) => {
    if (score >= 80) return { level: 'Excellent', color: 'text-green-600', bg: 'bg-green-500' };
    if (score >= 60) return { level: 'Good', color: 'text-blue-600', bg: 'bg-blue-500' };
    if (score >= 40) return { level: 'Fair', color: 'text-yellow-600', bg: 'bg-yellow-500' };
    return { level: 'Poor', color: 'text-red-600', bg: 'bg-red-500' };
  };

  const getFingerLabel = () => {
    if (!selectedOption) return 'Finger';
    return selectedOption.split('|')[1]?.replace(/([A-Z])/g, ' $1').trim() || 'Finger';
  };

  return (
    <div className="biochq-page">
      <div className="biochq-container">
        <Card className="biochq-card finger">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Gauge className="h-8 w-8 text-finger" />
              <CardTitle className="text-3xl font-bold text-gray-800">Finger Quality Checker</CardTitle>
            </div>
            <CardDescription className="text-gray-600">
              Analyze fingerprint image quality for biometric enrollment and verification
            </CardDescription>
          </CardHeader>

          <CardContent>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Input Section */}
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="fingerType" className="text-gray-700 font-medium">
                    Finger Type
                  </Label>
                  <Select
                    id="fingerType"
                    value={selectedOption}
                    onChange={(e) => setSelectedOption(e.target.value)}
                    className="w-full"
                  >
                    <option value="" disabled>Select finger type</option>
                    <option value="LEFT_INDEX_FINGER|Left IndexFinger">Left Index Finger</option>
                    <option value="LEFT_MIDDLE_FINGER|Left MiddleFinger">Left Middle Finger</option>
                    <option value="LEFT_RING_FINGER|Left RingFinger">Left Ring Finger</option>
                    <option value="LEFT_LITTLE_FINGER|Left LittleFinger">Left Little Finger</option>
                    <option value="LEFT_THUMB|Left Thumb">Left Thumb</option>
                    <option value="RIGHT_INDEX_FINGER|Right IndexFinger">Right Index Finger</option>
                    <option value="RIGHT_MIDDLE_FINGER|Right MiddleFinger">Right Middle Finger</option>
                    <option value="RIGHT_RING_FINGER|Right RingFinger">Right Ring Finger</option>
                    <option value="RIGHT_LITTLE_FINGER|Right LittleFinger">Right Little Finger</option>
                    <option value="RIGHT_THUMB|Right Thumb">Right Thumb</option>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="file" className="text-gray-700 font-medium">
                    Upload Fingerprint Image
                  </Label>
                  <div className="upload-zone">
                    <input
                      type="file"
                      id="file"
                      accept=".jpeg,.jpg,.bmp,.png,.jp2,.iso,.wsq"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <label htmlFor="file" className="cursor-pointer">
                      <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
                      <p className="text-xs text-gray-400 mt-1">PNG, JPG, BMP, JP2, WSQ, or ISO</p>
                    </label>
                  </div>
                </div>

                {file && injpegImageUrl && (
                  <div className="space-y-2">
                    <Label className="text-gray-700 font-medium">Preview</Label>
                    <div className="preview-container">
                      <img src={injpegImageUrl} alt="Uploaded fingerprint" className="preview-image" />
                      <Badge variant="finger" className="absolute top-2 right-2">{getFingerLabel()}</Badge>
                    </div>
                    <p className="text-xs text-gray-500 text-center">{file.name}</p>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    onClick={handleSubmit}
                    disabled={!file || !selectedOption || isProcessing}
                    variant="finger"
                    className="flex-1"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Gauge className="mr-2 h-4 w-4" />
                        Check Quality
                      </>
                    )}
                  </Button>
                  <Button onClick={handleReset} variant="outline" disabled={isProcessing}>
                    Reset
                  </Button>
                </div>
              </div>

              {/* Output Section */}
              <div className="space-y-6">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {score !== null && !error && (
                  <>
                    {/* Overall Score */}
                    {(() => {
                      const qualityLevel = getQualityLevel(score);
                      return (
                        <Card className={`border-2 ${
                          score >= 80 ? 'border-green-300 bg-green-50' :
                          score >= 60 ? 'border-blue-300 bg-blue-50' :
                          score >= 40 ? 'border-yellow-300 bg-yellow-50' : 'border-red-300 bg-red-50'
                        }`}>
                          <CardContent className="pt-6 text-center">
                            <div className="relative inline-flex items-center justify-center">
                              <svg className="w-32 h-32 transform -rotate-90">
                                <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="12" fill="none" className="text-gray-200" />
                                <circle
                                  cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="12" fill="none"
                                  strokeDasharray={`${score * 3.52} 352`}
                                  style={{ stroke: qualityLevel.bg.includes('green') ? '#22c55e' : 
                                           qualityLevel.bg.includes('blue') ? '#3b82f6' :
                                           qualityLevel.bg.includes('yellow') ? '#eab308' : '#ef4444' }}
                                />
                              </svg>
                              <div className="absolute">
                                <p className={`text-3xl font-bold ${qualityLevel.color}`}>{score}</p>
                                <p className="text-xs text-gray-500">out of 100</p>
                              </div>
                            </div>
                            <Badge className={`mt-4 ${qualityLevel.bg} text-white`}>{qualityLevel.level} Quality</Badge>
                          </CardContent>
                        </Card>
                      );
                    })()}

                    {/* Minutiae Details */}
                    {analyticsInfo && (
                      <Card className="bg-gray-50">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg">Minutiae Analysis</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Number of Minutiae:</span>
                            <span className="font-medium">{String(getAnalyticsValue(analyticsInfo, 'Number of Minutiae', 'numberOfMinutiae') ?? 'N/A')}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Reliability &gt; 0.5:</span>
                            <span className="font-medium">{String(getAnalyticsValue(analyticsInfo, 'Reliability Count > 0.5', 'reliabilityCountGt0_5') ?? 'N/A')}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Reliability &gt; 0.7:</span>
                            <span className="font-medium">{String(getAnalyticsValue(analyticsInfo, 'Reliability Count > 0.7', 'reliabilityCountGt0_7') ?? 'N/A')}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Reliability &gt; 0.9:</span>
                            <span className="font-medium">{String(getAnalyticsValue(analyticsInfo, 'Reliability Count > 0.9', 'reliabilityCountGt0_9') ?? 'N/A')}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Quality Map Foreground:</span>
                            <span className="font-medium">{String(getAnalyticsValue(analyticsInfo, 'Quality Map Foreground Count', 'qualityMapForegroundCount') ?? 'N/A')}</span>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Timing */}
                    {showAdvanced && (
                      <Card className="timing-card">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            Timing Details
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="grid grid-cols-2 gap-4 text-sm text-center">
                          <div>
                            <p className="text-gray-600">Display Conversion</p>
                            <p className="font-medium">{timing.convertInput.toFixed(2)} ms</p>
                          </div>
                          <div>
                            <p className="text-gray-600">ISO Conversion</p>
                            <p className="font-medium">{timing.convertIso.toFixed(2)} ms</p>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}

                {score === null && !error && submitted && (
                  <div className="flex items-center justify-center h-48 text-gray-400">
                    <Loader2 className="h-8 w-8 animate-spin" />
                  </div>
                )}

                {score === null && !error && !submitted && (
                  <div className="empty-state">
                    <Fingerprint className="h-12 w-12 mb-2 opacity-50" />
                    <p className="text-sm">Upload a fingerprint image to check quality</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default FingerQualityChecker;
